/**
 * Servicio para gestión de mecánicos a través de la nueva API
 * Centraliza todas las operaciones de mecánicos con la API backend
 */

import { apiHelpers } from './normalizedApiClient';
import { normalizeDataArray, normalizeDataObject } from '../utils/normalizationUtils';
import { API_ENDPOINTS } from '../config/api';
import { mecanicoTransforms } from '../utils/dataTransforms';

/**
 * Obtiene todos los mecánicos
 * @param {number} sucursalId - ID de la sucursal (requerido)
 * @returns {Promise<Array>} - Lista de mecánicos
 */
export const getMecanicos = async (sucursalId) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Obteniendo datos');

    
    if (!sucursalId) {

      return [];
    }
    
    const params = {
      sucursal_id: sucursalId
    };
    
    const response = await apiHelpers.mecanicos.get(API_ENDPOINTS.MECANICOS.BASE, { params });
    
    if (response.data) {
      const mecanicosData = response.data.data || response.data.mecanicos || response.data;

      return mecanicoTransforms.fromApiList(mecanicosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [MecanicosService] Error al obtener mecánicos:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío en lugar de fallar
    if (error.response?.status === 500) {
      console.warn('⚠️ [MecanicosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};

/**
 * Crea un nuevo mecánico
 * @param {Object} mecanico - Datos del mecánico
 * @returns {Promise<Object>} - Mecánico creado
 */
export const createMecanico = async (mecanico) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Creando registro');

    
    const mecanicoData = mecanicoTransforms.toApi(mecanico);
    const response = await apiHelpers.mecanicos.post(API_ENDPOINTS.MECANICOS.BASE, mecanicoData);
    
    if (response.data) {
      const mecanicoResponse = response.data.data || response.data.mecanico || response.data;

      return mecanicoTransforms.fromApi(mecanicoResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [MecanicosService] Error al crear mecánico:', error);
    throw error;
  }
};

/**
 * Actualiza un mecánico existente
 * @param {string|number} id - ID del mecánico
 * @param {Object} mecanico - Datos actualizados
 * @returns {Promise<Object>} - Mecánico actualizado
 */
export const updateMecanico = async (id, mecanico) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Actualizando registro');

    
    const mecanicoData = mecanicoTransforms.toApi(mecanico);
    const response = await apiHelpers.mecanicos.put(API_ENDPOINTS.MECANICOS.BY_ID(id), mecanicoData);
    
    if (response.data) {
      const mecanicoResponse = response.data.data || response.data.mecanico || response.data;
      //console.log('✅ [MecanicosService] Mecánico actualizado exitosamente');
      return mecanicoTransforms.fromApi(mecanicoResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [MecanicosService] Error al actualizar mecánico:', error);
    throw error;
  }
};

/**
 * Cambia el estado activo de un mecánico
 * @param {string|number} id - ID del mecánico
 * @param {boolean} activo - Nuevo estado activo
 * @returns {Promise<Object>} - Mecánico actualizado
 */
export const toggleMecanicoActive = async (id, activo) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Procesando operación');
    //console.log(`🔄 [MecanicosService] Cambiando estado del mecánico ${id} a ${activo ? 'activo' : 'inactivo'}...`);
    
    const response = await apiHelpers.mecanicos.patch(API_ENDPOINTS.MECANICOS.BY_ID(id), { activo });
    
    if (response.data) {
      const mecanicoResponse = response.data.data || response.data.mecanico || response.data;
      //console.log('✅ [MecanicosService] Estado del mecánico actualizado exitosamente');
      return mecanicoTransforms.fromApi(mecanicoResponse);
    }
    
    throw new Error('Respuesta inválida del servidor');
  } catch (error) {
    console.error('❌ [MecanicosService] Error al cambiar estado del mecánico:', error);
    throw error;
  }
};

/**
 * Elimina un mecánico (soft delete)
 * @param {string|number} id - ID del mecánico
 * @returns {Promise<string>} - ID del mecánico eliminado
 */
export const deleteMecanico = async (id) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Eliminando registro');
    //console.log(`🔄 [MecanicosService] Eliminando mecánico ${id}...`);
    
    await apiHelpers.mecanicos.delete(API_ENDPOINTS.MECANICOS.BY_ID(id));
    //console.log('✅ [MecanicosService] Mecánico eliminado exitosamente');
    
    return id.toString();
  } catch (error) {
    console.error('❌ [MecanicosService] Error al eliminar mecánico:', error);
    throw error;
  }
};

/**
 * Obtiene un mecánico por su ID
 * @param {string|number} id - ID del mecánico
 * @returns {Promise<Object|null>} - Mecánico encontrado o null
 */
export const getMecanicoById = async (id) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Obteniendo datos');
    //console.log(`🔍 [MecanicosService] Obteniendo mecánico ${id}...`);
    
    const response = await apiHelpers.mecanicos.get(API_ENDPOINTS.MECANICOS.BY_ID(id));
    
    if (response.data) {
      const mecanicoResponse = response.data.data || response.data.mecanico || response.data;
      //console.log('✅ [MecanicosService] Mecánico obtenido exitosamente');
      return mecanicoTransforms.fromApi(mecanicoResponse);
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      //console.log('📭 [MecanicosService] Mecánico no encontrado');
      return null;
    }
    console.error('❌ [MecanicosService] Error al obtener mecánico:', error);
    throw error;
  }
};

/**
 * Busca mecánicos por nombre o especialidad
 * @param {string} termino - Término de búsqueda
 * @param {number} sucursalId - ID de la sucursal (requerido)
 * @returns {Promise<Array>} - Lista de mecánicos encontrados
 */
export const buscarMecanicos = async (termino, sucursalId) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Procesando operación');
    //console.log(`🔍 [MecanicosService] Buscando mecánicos: "${termino}" en sucursal:`, sucursalId);
    
    if (!sucursalId) {
      console.warn('⚠️ [MecanicosService] sucursalId es requerido para búsqueda');
      return [];
    }
    
    const response = await apiHelpers.mecanicos.get(API_ENDPOINTS.MECANICOS.BASE, {
      params: { 
        q: termino,
        sucursal_id: sucursalId
      }
    });
    
    if (response.data) {
      const mecanicosData = response.data.data || response.data.mecanicos || response.data;
      //console.log(`✅ [MecanicosService] ${mecanicosData.length} mecánicos encontrados`);
      return mecanicoTransforms.fromApiList(mecanicosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [MecanicosService] Error al buscar mecánicos:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío
    if (error.response?.status === 500) {
      console.warn('⚠️ [MecanicosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};

/**
 * Obtiene mecánicos por especialidad
 * @param {string} especialidad - Especialidad
 * @param {number} sucursalId - ID de la sucursal (requerido)
 * @returns {Promise<Array>} - Lista de mecánicos de la especialidad
 */
export const getMecanicosPorEspecialidad = async (especialidad, sucursalId) => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Obteniendo datos');
    //console.log(`🔍 [MecanicosService] Obteniendo mecánicos de especialidad "${especialidad}" en sucursal:`, sucursalId);
    
    if (!sucursalId) {
      console.warn('⚠️ [MecanicosService] sucursalId es requerido para filtrar por especialidad');
      return [];
    }
    
    const response = await apiHelpers.mecanicos.get(API_ENDPOINTS.MECANICOS.BASE, {
      params: { 
        especialidad,
        sucursal_id: sucursalId
      }
    });
    
    if (response.data) {
      const mecanicosData = response.data.data || response.data.mecanicos || response.data;
      //console.log(`✅ [MecanicosService] ${mecanicosData.length} mecánicos de especialidad obtenidos`);
      return mecanicoTransforms.fromApiList(mecanicosData);
    }
    return [];
  } catch (error) {
    console.error('❌ [MecanicosService] Error al obtener mecánicos por especialidad:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío
    if (error.response?.status === 500) {
      console.warn('⚠️ [MecanicosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};

/**
 * Obtiene las especialidades disponibles
 * @returns {Promise<Array>} - Lista de especialidades únicas
 */
export const getEspecialidades = async () => {
  try {
    //console.log('👨‍🔧 [MecanicosService] Obteniendo datos');
    //console.log('🔍 [MecanicosService] Obteniendo especialidades disponibles...');
    
    const response = await apiHelpers.mecanicos.get(`${API_ENDPOINTS.MECANICOS.BASE}/especialidades`);
    
    if (response.data) {
      const especialidadesData = response.data.data || response.data.especialidades || response.data;
      //console.log(`✅ [MecanicosService] ${especialidadesData.length} especialidades disponibles`);
      return especialidadesData;
    }
    return [];
  } catch (error) {
    console.error('❌ [MecanicosService] Error al obtener especialidades:', error);
    
    // Si hay un error 500 del servidor, devolver array vacío
    if (error.response?.status === 500) {
      console.warn('⚠️ [MecanicosService] Fallback: Devolviendo array vacío debido a error 500');
      return [];
    }
    
    throw error;
  }
};